import React, {BaseSyntheticEvent, useCallback, useEffect, useMemo, useRef} from 'react';
import './GameIframe.css';

interface GameIframeProps {
    iframeSrc: string;
    showControls: boolean;
    onClose: () => void;
}

const GameIframe: React.FC<GameIframeProps> = ({ iframeSrc, showControls, onClose }) => {
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const iframeWindowRef = useRef<Window | null>(null);
    const lastSpeedRef = useRef<number>(0);

    const soundButtonRef = useRef<HTMLButtonElement>(null);
    const menuButtonRef = useRef<HTMLButtonElement>(null);
    const paytableButtonRef = useRef<HTMLButtonElement>(null);
    const helpButtonRef = useRef<HTMLButtonElement>(null);
    const speedButtonRef = useRef<HTMLButtonElement>(null);

    const gameSpeeds = useMemo<Array<string>>(() => ['normal', 'medium', 'high'], []);

    const handleClose = useCallback(() => {
        document.body.style.overflow = 'auto';
        onClose();
    }, [onClose]);

    const handleMessage = (event: MessageEvent) => {
        // Security check: only accept messages from expected origin if you know it
        if (!event.origin.endsWith('game2slot.dev')) {
            return;
        }

        const message = event.data

        switch (message.type) {
            case 'g2s-ps.game-sound-update':
                if (soundButtonRef.current) {
                    if (message.data.toggleValue) {
                        soundButtonRef.current.classList.remove('toggle-on');
                        soundButtonRef.current.innerHTML = '&#x1F508;';
                    } else {
                        soundButtonRef.current.classList.add('toggle-on');
                        soundButtonRef.current.innerHTML = '&#x1F507;';
                    }
                }
                break;
            case 'g2s-ps.game-toggle-menu':
                if (menuButtonRef.current) {
                    if (message.data.toggleValue) {
                        menuButtonRef.current.classList.add('toggle-on');
                        menuButtonRef.current.innerHTML = '&ne;';
                    } else {
                        menuButtonRef.current.classList.remove('toggle-on');
                        menuButtonRef.current.innerHTML = '&#8801;';
                    }
                }
                break;
            case 'g2s-ps.game-toggle-paytable':
                if (paytableButtonRef.current) {
                    if (message.data.toggleValue) {
                        paytableButtonRef.current.classList.add('toggle-on');
                    } else {
                        paytableButtonRef.current.classList.remove('toggle-on');
                    }
                }
                break;
            case 'g2s-ps.game-toggle-help':
                if (helpButtonRef.current) {
                    if (message.data.toggleValue) {
                        helpButtonRef.current.classList.add('toggle-on');
                    } else {
                        helpButtonRef.current.classList.remove('toggle-on');
                    }
                }
                break;
            case 'g2s-ps.game-fast-play-update':
                if (speedButtonRef.current) {
                    lastSpeedRef.current = gameSpeeds.indexOf(message.data.fastPlayUpdate);
                    speedButtonRef.current.innerHTML = gameSpeeds[lastSpeedRef.current].charAt(0).toUpperCase();
                    speedButtonRef.current.dataset.speed = gameSpeeds[lastSpeedRef.current];
                }
                break
            case 'g2s-ps.exit':
                handleClose();
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        let iframeEl: HTMLIFrameElement | null = null;

        const handleLoad = () => {
            if (iframeEl) {
                iframeWindowRef.current = iframeEl.contentWindow;
            }
        };

        if (iframeSrc.length > 0) {
            iframeEl = iframeRef.current;
            iframeEl?.addEventListener('load', handleLoad);
            window.addEventListener('message', handleMessage);
            document.body.style.overflow = 'hidden';
        } else {
            window.removeEventListener('message', handleMessage);
            document.body.style.overflow = 'auto';
        }

        return () => {
            iframeEl?.removeEventListener('load', handleLoad);
            window.removeEventListener('message', handleMessage);
            iframeWindowRef.current = null;
            iframeEl = null;

            document.body.style.overflow = 'auto';
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleClose, iframeSrc, gameSpeeds]);

    const postToIframe = (data: any) => {
        iframeWindowRef.current?.postMessage(data, '*');
    };

    const toggleSound = (e: BaseSyntheticEvent) => {
        let toggleValue: boolean;

        toggleValue = e.target.classList.contains('toggle-on');

        postToIframe({ type: 'g2s-ps.game-sound-update', data: { toggleValue } });
    }

    const toggleMenu = (e: BaseSyntheticEvent) => {
        let toggleValue: boolean;

        toggleValue = !e.target.classList.contains('toggle-on');

        postToIframe({ type: 'g2s-ps.game-toggle-menu', data: { toggleValue } });
    }

    const togglePaytable = (e: BaseSyntheticEvent) => {
        let toggleValue: boolean;

        toggleValue = !e.target.classList.contains('toggle-on');

        postToIframe({ type: 'g2s-ps.game-toggle-paytable', data: { toggleValue } });
    }

    const toggleHelp = (e: BaseSyntheticEvent) => {
        let toggleValue: boolean;

        toggleValue = !e.target.classList.contains('toggle-on');

        postToIframe({ type: 'g2s-ps.game-toggle-help', data: { toggleValue } });
    }

    const changeSpeed = () => {
        const newSpeedIndex = (lastSpeedRef.current + 1) % gameSpeeds.length

        postToIframe({ type: 'g2s-ps.game-fast-play-update', data: { fastPlayUpdate: gameSpeeds[newSpeedIndex] } });
    }

    if (iframeSrc.length === 0) {
        return null;
    }

    return (
        <div className="iframe-container">
            <iframe
                title="Game iframe"
                key={iframeSrc.length}
                id="ID_GAME_FRAME"
                className="game-iframe"
                src={iframeSrc}
                ref={iframeRef}
                allow="autoplay; fullscreen; screen-wake-lock; display-capture"
                allowFullScreen
                style={{ width: '100%', height: '100%', border: 'none' }}
            />
            <button
                className="integration-button close-iframe-button"
                onClick={handleClose}
                title="Close Game"
            >
                ✕
            </button>
            {showControls && (
                <>
                    <button
                        className="integration-button sound-mute-button"
                        ref={soundButtonRef}
                        onClick={toggleSound}
                        title="Toggle Sound"
                    >
                        &#x1F508;
                    </button>
                    <button
                        className="integration-button menu-button"
                        ref={menuButtonRef}
                        onClick={toggleMenu}
                        title="Toggle Menu"
                    >
                        &#8801;
                    </button>
                    <button
                        className="integration-button paytable-button"
                        ref={paytableButtonRef}
                        onClick={togglePaytable}
                        title="Toggle Paytable"
                    >
                        $
                    </button>
                    <button
                        className="integration-button help-button"
                        ref={helpButtonRef}
                        onClick={toggleHelp}
                        title="Toggle Help"
                    >
                        ?
                    </button>
                    <button
                        className="integration-button speed-button"
                        ref={speedButtonRef}
                        onClick={changeSpeed}
                        title="Change Game Speed"
                        data-speed="normal"
                    >
                        N
                    </button>
                </>
            )}
        </div>
    );
};

export default GameIframe;
